import React from 'react';
import { Document, Font, Page, PDFDownloadLink, Text, View, Image } from '@react-pdf/renderer';
import { createTw } from 'react-pdf-tailwind';
import _ from 'lodash';
import TW_GLOBAL from '../../tailwind.config';
import LOGO from '../images/dwtc-logo-pdf-v2.png';
import DINRegular from '../fonts/URWDIN-Regular.ttf';
import DINMedium from '../fonts/URWDIN-Medium.ttf';

function getCurrentDateTime() {
  const timestamp = _.now();
  const date = new Date(timestamp);

  // Format date as MM/DD/YYYY
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  // Format time as HH:MM
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  // Combine date and time with separator
  return `This document was duly generated on ${month}/${day}/${year}, at  ${hours}:${minutes}.`
}

export default function FreeZoneActivityTablePdf({ data, selectedFilter, keywords }) {
  Font.register({
    family: 'DINRegular',
    src: DINRegular,
    fontWeight: 'normal',
  });

  Font.register({
    family: 'DINMedium',
    src: DINMedium,
    fontWeight: 'medium',
  });

  Font.register({
    family: 'sans-serif',
    src: DINMedium,
    fontWeight: 'normal',
  });

  Font.register({
    family: 'DINMedium, sans-serif',
    src: DINMedium,
    fontWeight: 'normal',
  });

  Font.register({
    family: 'DINRegular, sans-serif',
    src: DINRegular,
    fontWeight: 'normal',
  });

  const rows = _.get(data, 'rows', []);
  const columnNames = _.get(data, 'columnNames', []);
  const chunkGroup = _.chunk(rows, 8);

  const tw = createTw(TW_GLOBAL);

  const columnsWidth = [
    11.077471609939155, 8.077471609939155, 9.077471609939155, 11.077471609939155,
    47.793350189633744, 12.896763370609636,
  ];

  return (
    <Document>
      {_.map(chunkGroup, (chunk, chuckIndex) => (
        <Page
          size={[1024, 724]}
          style={tw(
            'bg-white w-full h-screen flex flex-col p-[2.5vw] border-b gap-[2vw] justify-start',
          )}
          language="en-us">
          <View style={tw('w-full flex h-auto items-center flex-col gap-[2vw]')}>
            <View style={tw('w-full flex h-auto items-center flex-row justify-between')}>
              <Image alt="logo" style={tw('w-[6.8vw] object-contain z-50 block')} src={LOGO} />
              <Text style={tw('font-DINMedium text-[1.5vw] text-[#000000]')}>
                Business Activity List
              </Text>
            </View>
            <View
              style={tw(
                'w-full flex flex-row h-auto justify-center py-[0.5vw] mx-auto items-center gap-[1.75vw]',
              )}>
              <View style={tw('w-aut0 h-auto flex flex-row gap-[1.75vw] items-center')}>
                <Text
                  style={tw('font-DINMedium text-[0.7125vw] leading-[1] text-[#231F20] uppercase')}>
                  Filters applied:
                </Text>
                <View style={tw('flex flex-row gap-6')}>
                  {selectedFilter && (
                    <Text style={tw('font-DINRegular leading-[1] text-[0.6532vw] text-[#4D4D4D]')}>
                      {`License Type: ${selectedFilter}`}
                    </Text>
                  )}
                  {keywords && Boolean(selectedFilter) && (
                    <View style={tw('w-[0.4vw] h-[0.4vw] rounded-full bg-black')} />
                  )}
                  {keywords && (
                    <Text style={tw('font-DINRegular leading-[1] text-[0.6532vw] text-[#4D4D4D]')}>
                      {`Keywords: ${keywords}`}
                    </Text>
                  )}
                  {(!keywords && !selectedFilter) && (
                    <Text style={tw('font-DINRegular leading-[1] text-[0.6532vw] text-[#4D4D4D]')}>
                      No filters applied
                    </Text>
                  )}
                </View>
              </View>
            </View>
          </View>

          <View style={tw('w-full flex flex-col grow')}>
            <View
              style={tw(
                'flex py-[0.7125vw] w-full bg-[#F7F7F7] border-b border-[#CE435B] flex-row',
              )}>
              {_.map(columnNames, (columnName, index) => (
                <Text
                  style={tw(
                    `w-[${columnsWidth[index]}%] uppercase font-DINMedium text-[0.7125vw] leading-[1.1] flex items-center h-auto px-[1.18vw]`,
                  )}>
                  {columnName}
                </Text>
              ))}
            </View>
            <View>
              {_.map(chunk, (row, rowIndex) => (
                <View
                  style={tw(
                    `w-full flex min-h-[5.7vw] py-[0.5vw] border-b border-r border-l border-[#E6E6E6] flex-row items-center bg-[${rowIndex % 2 === 0 ? '#FFF' : '#F7F7F7'}]`,
                  )}>
                  {_.map(row, (cell, cellIndex) => (
                    <Text
                      style={tw(
                        `w-[${columnsWidth[cellIndex]}%] text-[0.7125vw] leading-[1.4] flex items-center px-[1.18vw] font-DINRegular text-[#4D4D4D]`,
                      )}>
                      {cell}
                    </Text>
                  ))}
                </View>
              ))}
            </View>
          </View>
          {/* <View style={tw('w-full flex flex-col grow')} /> */}
          <View style={tw('w-full flex h-auto items-center flex-row justify-between')}>
            <View style={tw('w-auto flex h-auto items-center flex-row gap-[0.5vw]')}>
              <Text
                style={tw('font-DINMedium text-[#231F20] text-[0.7125vw] leading-[1] uppercase')}>
                {getCurrentDateTime()}
              </Text>
            </View>
            <Text style={tw('font-DINMedium text-[#231F20] text-[0.7125vw] leading-[1] uppercase')}>
              {`Page ${_.padStart((chuckIndex + 1).toString(), 2, '0')} of ${_.size(chunkGroup)}`}
            </Text>
          </View>
        </Page>
      ))}
    </Document>
  );
}
